'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/slider/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { clampSliderValue, SLIDER_NOTCH_COUNT, type SliderVariant, toLitNotches, toSliderPercent } from 'BomdisoftComponents/inputs/slider/render';
import { type ComponentProps, type CSSProperties, type KeyboardEvent, type ReactNode, useId, useState } from 'react';

export type { SliderVariant } from 'BomdisoftComponents/inputs/slider/render';
export { clampSliderValue } from 'BomdisoftComponents/inputs/slider/render';

export type SliderProps = Omit<ComponentProps<'input'>, 'defaultValue' | 'max' | 'min' | 'onChange' | 'step' | 'type' | 'value'> & {
  label?: ReactNode;
  max?: number;
  min?: number;
  onChange: (value: number) => void;
  readoutLabel?: string;
  step?: number;
  value: number;
  variant?: SliderVariant;
};

const NOTCH_KEYS = Array.from({ length: SLIDER_NOTCH_COUNT }, (_, index) => `notch-${index}`);

export const Slider = ({ className, label, max = 100, min = 0, onChange, readoutLabel = 'Value', step = 1, value, variant = 'channel', ...rest }: SliderProps) => {
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;
  const [draft, setDraft] = useState<string | null>(null);
  const percent = toSliderPercent(value, min, max);
  const litNotches = toLitNotches(percent);
  const commitDraft = () => {
    if (draft === null) {
      return;
    }
    onChange(clampSliderValue(Number.parseInt(draft, 10), min, max, step));
    setDraft(null);
  };
  const handleRangeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!event.shiftKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return;
    }
    event.preventDefault();
    const delta = event.key === 'ArrowRight' ? 10 * step : -10 * step;
    onChange(clampSliderValue(value + delta, min, max, step));
  };
  const control = (
    <span className={joinClasses('bomdisoft-slider', !label && className)} data-disabled={rest.disabled ? '' : undefined} data-variant={variant} style={{ '--slider-value': `${percent}%` } as CSSProperties}>
      <span className='bomdisoft-slider-track'>
        {variant === 'notched' && (
          <span aria-hidden='true' className='bomdisoft-slider-notches'>
            {NOTCH_KEYS.map((key, index) => (
              <span className='bomdisoft-slider-notch' data-notch='' data-notch-on={index < litNotches ? '' : undefined} key={key} />
            ))}
          </span>
        )}
        <input {...rest} className='bomdisoft-slider-range' id={inputId} max={max} min={min} onChange={event => onChange(Number(event.target.value))} onKeyDown={handleRangeKeyDown} step={step} type='range' value={value} />
      </span>
      <input
        aria-label={readoutLabel}
        className='bomdisoft-slider-readout'
        disabled={rest.disabled}
        inputMode='numeric'
        onBlur={commitDraft}
        onChange={event => setDraft(event.target.value.replace(/[^\d-]/g, ''))}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            commitDraft();
          }
        }}
        value={draft ?? String(value)}
      />
    </span>
  );
  if (!label) {
    return control;
  }
  return (
    <label className={joinClasses('bomdisoft-slider-label', className)} data-disabled={rest.disabled ? '' : undefined} htmlFor={inputId}>
      <span className='bomdisoft-slider-label-text'>{label}</span>
      {control}
    </label>
  );
};
